import { useAtomValue, useSetAtom } from 'jotai';
import DriveCallReceiver from './DriveCallReceiver';
import { allVehiclesSelector } from '@data/fms/vehicle/states';
import { remoteMonitoringSettingAtom } from '@data/fms/environment/states';
import { useMount, useUnmount } from 'react-use';
import { useRemoteDevices } from '@api/media';
import { useCallback, useMemo, useState } from 'react';
import type { MediaClient } from '@tier4/webauto-media-client';
import { MAX_ALERT_NUM, useNotification } from '@data/notification';
import { vehicleAlertListAtom } from '@data/notification/vehicle';
import type { VehicleAlert } from '@data/notification/vehicle/types';
import { DateTime } from 'luxon';
import { VehicleAlertContent } from '@components/common/molecules';
// import type {
//   RTCRequestSubscriber,
//   RTCRequest,
  // MRMNotificationSubscriber,
  // MRMNotification,
  // StandbyManager,
  // HeartbeatStatus,
// } from '@tier4/webauto-remote-client';
import type { MediaQualityTransitionEvent } from '@tier4/webauto-media-client/dist/domain/MediaQualityEvent';
import { FEATURE_SCOPES, SCOPES, useHasScope } from '@data/auth';
import { FeatureToggle, ToggleOn } from '../atoms';

const DriveNotification = () => {
  const { notifyAlert } = useNotification();
  const {
    getMediaQualityMonitor,
    releaseMediaQualityMonitor,
    // getRTCRequestSubscriber,
    // releaseRTCRequestSubscriber,
    // getMRMNotificationSubscriber,
    // releaseMRMNotificationSubscriber,
    // getStandbyManager,
    // releaseStandbyManager,
  } = useRemoteDevices();
  const remoteMonitoringSettings = useAtomValue(remoteMonitoringSettingAtom);
  const allVehicles = useAtomValue(allVehiclesSelector);
  const [monitor, setMonitor] = useState<MediaClient.MediaQualityMonitor>();
  // const [rtcSubscriber, setRtcSubscriber] = useState<RTCRequestSubscriber>();
  // const [mrmSubscriber, setMrmSubscriber] =
  //   useState<MRMNotificationSubscriber>();
  // const [standbyManager, setStandbyManager] = useState<StandbyManager>();
  const setVehicleAlertList = useSetAtom(vehicleAlertListAtom);
  const getHasScope = useHasScope();

  const hasMonitoringScope = useMemo(
    () =>
      getHasScope([
        SCOPES.DescribeMonitoringRule,
        SCOPES.DescribeMonitoringAlert,
      ]),
    [getHasScope],
  );

  const notify = useCallback(
    ({
      variant,
      alertOption,
    }: {
      variant: 'error';
      alertOption: VehicleAlert;
    }) => {
      notifyAlert({
        variant: variant,
        alert: alertOption,
        message: (
          <div data-alert="true">
            <VehicleAlertContent alert={alertOption} />
          </div>
        ),
        enterFunction: () => {
          setVehicleAlertList((prevState) => {
            const clone = prevState.concat();
            if (clone.length === MAX_ALERT_NUM) {
              clone.pop();
            }
            const id = `${new Date().getTime()}_${alertOption.type}_${
              alertOption.vehicle.vehicle_id
            }`;
            return [{ ...alertOption, id }, ...clone];
          });
        },
      });
    },
    [notifyAlert, setVehicleAlertList],
  );

  const handleMediaQualityTransition = useCallback(
    (e: MediaQualityTransitionEvent) => {
      if (
        e.state !== 'ALERTING' ||
        !remoteMonitoringSettings?.value.notification
      ) {
        return;
      }
      const vehicle = allVehicles.find((v) => v.vehicle_id === e.vehicle_id);
      if (!vehicle) return;
      const alertOption: VehicleAlert = {
        type:
          e.device_type === 'camera'
            ? 'cameraQualityDeterioration'
            : 'audioQualityDeterioration',
        notifiedAt: DateTime.now().toFormat('yyyy/MM/dd HH:mm:ss'),
        vehicle,
      };
      notify({
        variant: 'error',
        alertOption,
      });
    },
    [notify, allVehicles, remoteMonitoringSettings?.value.notification],
  );

  // const handleRTCSubscribe = useCallback(
  //   (e: RTCRequest[]) => {
  //     if (!remoteMonitoringSettings?.value.notification) {
  //       return;
  //     }
  //     for (const request of e) {
  //       const vehicle = allVehicles.find(
  //         (v) => v.vehicle_id === request.vehicleId,
  //       );
  //       if (vehicle) {
  //         const alertOption: VehicleAlert = {
  //           type: 'rtc',
  //           notifiedAt: DateTime.now().toFormat('yyyy/MM/dd HH:mm:ss'),
  //           vehicle,
  //         };
  //         notify({
  //           variant: 'error',
  //           alertOption,
  //         });
  //       }
  //     }
  //   },
  //   [remoteMonitoringSettings?.value.notification, allVehicles, notify],
  // );

  // const handleMRMSubscribe = useCallback(
  //   (e: MRMNotification) => {
  //     if (!remoteMonitoringSettings?.value.notification) {
  //       return;
  //     }
  //     const vehicle = allVehicles.find(
  //       (v) => v.vehicle_id === e.vehicleId,
  //     );
  //     if (!vehicle) return;
  //     const alertOption: VehicleAlert = {
  //       type: 'mrm',
  //       notifiedAt: DateTime.now().toFormat('yyyy/MM/dd HH:mm:ss'),
  //       vehicle,
  //     };
  //     notify({
  //       variant: 'error',
  //       alertOption,
  //     });
  //   },
  //   [remoteMonitoringSettings?.value.notification, allVehicles, notify],
  // );

  // const handleHeartbeat = useCallback(
  //   (status: HeartbeatStatus, vehicleId?: string) => {
  //     if (
  //       !remoteMonitoringSettings?.value.notification ||
  //       (status !== 'rejected' && status !== 'disconnected')
  //     ) {
  //       return;
  //     }
  //     const vehicle = allVehicles.find((v) => v.vehicle_id === vehicleId);
  //     if (!vehicle) return;
  //     const alertOption: VehicleAlert = {
  //       type:
  //         status === 'rejected' ? 'heartbeatRejected' : 'heartbeatDisconnected',
  //       notifiedAt: DateTime.now().toFormat('yyyy/MM/dd HH:mm:ss'),
  //       vehicle,
  //     };
  //     notify({
  //       variant: 'error',
  //       alertOption,
  //     });
  //   },
  //   [allVehicles, notify, remoteMonitoringSettings?.value.notification],
  // );

  useMount(() => {
    if (hasMonitoringScope) {
      const _monitor = getMediaQualityMonitor();
      setMonitor(_monitor);
      if (_monitor) {
        _monitor.addEventListener(
          'media-quality-transition',
          handleMediaQualityTransition,
        );
      }
    }
    // const _rtcSubscriber = getRTCRequestSubscriber();
    // setRtcSubscriber(_rtcSubscriber);
    // if (_rtcSubscriber) {
    //   _rtcSubscriber.addRequestListener(handleRTCSubscribe);
    // }
    // const _mrmSubscriber = getMRMNotificationSubscriber();
    // setMrmSubscriber(_mrmSubscriber);
    // if (_mrmSubscriber) {
    //   _mrmSubscriber.addListener(handleMRMSubscribe);
    // }
    // const _standbyManager = getStandbyManager();
    // setStandbyManager(_standbyManager);
    // if (_standbyManager) {
    //   _standbyManager.addEventListener('heartbeat', handleHeartbeat);
    //   _standbyManager.startStandby();
    // }
  });

  useUnmount(() => {
    if (monitor) {
      monitor.removeEventListener(
        'media-quality-transition',
        handleMediaQualityTransition,
      );
      releaseMediaQualityMonitor(monitor);
    }
    // if (rtcSubscriber) {
    //   rtcSubscriber.removeRequestListener(handleRTCSubscribe);
    //   releaseRTCRequestSubscriber(rtcSubscriber);
    // }
    // if (mrmSubscriber) {
    //   mrmSubscriber.removeListener(handleMRMSubscribe);
    //   releaseMRMNotificationSubscriber(mrmSubscriber);
    // }
    // if (standbyManager) {
    //   standbyManager.stopStandby();
    //   standbyManager.removeEventListener('heartbeat', handleHeartbeat);
    //   releaseStandbyManager(standbyManager);
    // }
  });

  if (!remoteMonitoringSettings?.value.notification) {
    return null;
  }

  return (
    <FeatureToggle type="featureScope" scope={FEATURE_SCOPES.VoiceCall}>
      <ToggleOn>
        <FeatureToggle scope={SCOPES.Call}>
          <ToggleOn>
            {allVehicles.map((vehicle) => (
              <DriveCallReceiver key={vehicle.vehicle_id} vehicle={vehicle} />
            ))}
          </ToggleOn>
        </FeatureToggle>
      </ToggleOn>
    </FeatureToggle>
  );
};

export default DriveNotification;
